import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

const STATUS_TONE = {
  success: { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "✓ Success" },
  iterating: { tone: "bg-amber-50 text-amber-700 border-amber-200", label: "↻ Iterating" },
  scrapped: { tone: "bg-rose-50 text-rose-700 border-rose-200", label: "✗ Scrapped" },
};

const TIMELINE = [
  { n: "01", title: "Idea", body: "A customer comment, a chef's note, a fruit-market observation." },
  { n: "02", title: "Research", body: "We study ingredients, freezing science and flavour chemistry." },
  { n: "03", title: "Prototype", body: "A small batch — sometimes hideous, often surprising." },
  { n: "04", title: "Testing", body: "Internal panel, blind tasting, customer feedback at Pop Panda Go." },
  { n: "05", title: "Improvement", body: "Tweak ratios, swap stabilisers, simplify the label." },
  { n: "06", title: "Final Product", body: "Bottled into the freezer — or honoured in the Failed Recipe Wall." },
];

export default function InnovationLab() {
  const [protos, setProtos] = useState([]);
  useEffect(() => {
    api.get("/prototypes").then((r) => setProtos(r.data || []));
  }, []);

  return (
    <div data-testid="lab-page" className="section-pad">
      <div className="container-pp">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Innovation Lab</div>
        <h1 className="editorial-h1 text-5xl md:text-7xl mt-3">The recipes we got wrong, on purpose.</h1>
        <p className="mt-6 max-w-2xl text-brand-charcoal/70 text-lg leading-relaxed">
          Every product on our shelf started as a problem. We document our prototypes — including
          the ones we scrapped — because the lab is the story.
        </p>

        {/* Timeline */}
        <div className="mt-16">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-6">Product Evolution Timeline</div>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
            {TIMELINE.map((t) => (
              <li key={t.n} className="card-elevated p-5">
                <div className="text-[11px] tracking-[0.22em] text-brand-red">{t.n}</div>
                <div className="font-display text-xl mt-2">{t.title}</div>
                <p className="mt-2 text-xs text-brand-charcoal/65 leading-relaxed">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Prototypes */}
        <div className="mt-20">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mb-6">Failed & Featured Prototypes</div>
          <div className="grid lg:grid-cols-2 gap-6" data-testid="lab-prototypes">
            {protos.map((p) => {
              const s = STATUS_TONE[p.status] || STATUS_TONE.iterating;
              return (
                <article key={p.id} data-testid={`proto-${p.id}`} className="card-elevated overflow-hidden">
                  <div className="grid sm:grid-cols-5">
                    {p.image_url && (
                      <div className="sm:col-span-2 aspect-[4/3] sm:aspect-auto">
                        <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" />
                      </div>
                    )}
                    <div className={`p-6 ${p.image_url ? "sm:col-span-3" : "sm:col-span-5"}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">{p.prototype_number}</span>
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${s.tone}`}>{s.label}</span>
                      </div>
                      <h3 className="font-display text-3xl mt-2 leading-tight">{p.title}</h3>
                      <dl className="mt-4 space-y-3 text-sm">
                        <div><dt className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55">Problem</dt><dd className="text-brand-charcoal/80">{p.problem}</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55">What went wrong</dt><dd className="text-brand-charcoal/80">{p.what_went_wrong}</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55">How we improved</dt><dd className="text-brand-charcoal/80">{p.how_improved}</dd></div>
                        <div><dt className="text-[10px] uppercase tracking-[0.2em] text-brand-charcoal/55">Final learning</dt><dd className="text-brand-charcoal/80">{p.final_learning}</dd></div>
                      </dl>
                    </div>
                  </div>
                </article>
              );
            })}
            {protos.length === 0 && <div className="text-brand-charcoal/60">Prototypes will appear here as our lab publishes them.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
